// src/LogPanel.js
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import './LogPanel.css';

const SCROLLBALL_DIAMETER = 17;

const LogPanel = ({ logHistory = [], initialBoardText = null, chatEnabled = false, chatMessages = [], chatInput = '', onChatInputChange, onChatSend, currentMoveIndex = 0, llmThinking = false, llmProvider = null, llmModel = null, llmError = null, playerConfig = {}, tutorialMode = null, puzzleMetadata = null, puzzleEvalResult = null, puzzleAttemptMode = 'manual' }) => {
    // Helper to get player display name
    const getPlayerName = (playerNum) => {
        const key = playerNum === 1 ? 'p1' : 'p2';
        const provider = playerConfig[key];
        if (!provider || provider === 'Human') return 'Human';
        // Show provider + model if available
        if (playerConfig.modelName) {
            return `${provider} (${playerConfig.modelName})`;
        }
        return provider;
    };
    const [mode, setMode] = useState(tutorialMode ? 'chat' : 'log');
    const contentRef = useRef(null);
    const scrollTrackRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [scrollTop, setScrollTop] = useState(0);
    const [trackHeight, setTrackHeight] = useState(0);
    const [dimensions, setDimensions] = useState({
        scrollHeight: 0,
        clientHeight: 0,
        maxScrollTop: 0,
    });
    const prevLogLengthRef = useRef(logHistory.length);

    // Track which items have expanded reasoning (turn-level or action-level)
    const [expandedReasoning, setExpandedReasoning] = useState(new Set());

    const toggleReasoningExpand = (key) => {
        setExpandedReasoning(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // Measure dimensions when content changes
    useLayoutEffect(() => {
        const container = contentRef.current;
        const track = scrollTrackRef.current;
        if (!container) return;

        const measure = () => {
            const { scrollHeight, clientHeight } = container;
            const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
            setDimensions({ scrollHeight, clientHeight, maxScrollTop });
            setScrollTop(container.scrollTop);
            if (track) {
                setTrackHeight(track.clientHeight);
            }
        };

        measure();

        const handleScroll = () => setScrollTop(container.scrollTop);
        container.addEventListener('scroll', handleScroll, { passive: true });

        let resizeObserver;
        if (typeof ResizeObserver === 'function') {
            resizeObserver = new ResizeObserver(measure);
            resizeObserver.observe(container);
            if (track) {
                resizeObserver.observe(track);
            }
        }

        return () => {
            container.removeEventListener('scroll', handleScroll);
            resizeObserver?.disconnect();
        };
    }, [mode, expandedReasoning, logHistory.length, initialBoardText]);

    // Scroll tutorial panel to top on step change
    useEffect(() => {
        if (tutorialMode && contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [tutorialMode?.stepIndex]);

    // Auto-scroll to bottom when new log entries are added (skip in tutorial mode)
    useEffect(() => {
        const container = contentRef.current;
        if (!container || tutorialMode) return;

        // Only auto-scroll when log grows (new moves added)
        if (logHistory.length > prevLogLengthRef.current) {
            container.scrollTop = container.scrollHeight;
        }
        prevLogLengthRef.current = logHistory.length;
    }, [logHistory.length, tutorialMode]);

    // Calculate thumb position inline during render
    const thumbHeight = SCROLLBALL_DIAMETER;
    const thumbRange = Math.max(trackHeight - thumbHeight, 0);
    const { maxScrollTop } = dimensions;
    const thumbPosition = (maxScrollTop > 0 && thumbRange > 0)
        ? (scrollTop / maxScrollTop) * thumbRange
        : 0;

    const handleThumbPointerDown = (event) => {
        event.preventDefault();
        const container = contentRef.current;
        const trackEl = scrollTrackRef.current;
        if (!container || !trackEl) return;

        const currentMaxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
        if (currentMaxScrollTop <= 0) return;

        setIsDragging(true);
        const pid = event.pointerId;
        event.target.setPointerCapture(pid);
        const startY = event.clientY;
        const startScrollTop = container.scrollTop;
        const currentTrackHeight = trackEl.clientHeight;
        const scrollableDist = Math.max(currentTrackHeight - thumbHeight, 1);
        const scrollRatio = currentMaxScrollTop / scrollableDist;

        const handlePointerMove = (moveEvent) => {
            const deltaY = moveEvent.clientY - startY;
            const next = Math.max(0, Math.min(currentMaxScrollTop, startScrollTop + (deltaY * scrollRatio)));
            container.scrollTop = next;
        };

        const handlePointerUp = () => {
            setIsDragging(false);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
    };

    const handleTrackClick = (e) => {
        // Ignore if clicking on the thumb itself
        if (e.target.classList.contains('panel-scroll-thumb')) return;

        const container = contentRef.current;
        const trackEl = scrollTrackRef.current;
        if (!container || !trackEl) return;

        const currentMaxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
        if (currentMaxScrollTop <= 0) return;

        const trackRect = trackEl.getBoundingClientRect();
        const clickY = e.clientY - trackRect.top;
        const trackHeight = trackRect.height;
        const scrollRatio = clickY / trackHeight;
        const targetScroll = Math.max(0, Math.min(currentMaxScrollTop, scrollRatio * currentMaxScrollTop));

        container.scrollTop = targetScroll;
    };

    const renderLogContent = () => {
        // Show placeholder if no history and no initial board
        if ((!logHistory || logHistory.length === 0) && !initialBoardText) {
            return (
                <div className="log-empty">
                    <span className="log-empty-text">{tutorialMode ? 'Game log will appear here after you advance...' : 'Game log will appear here...'}</span>
                </div>
            );
        }

        return (
            <div className="log-typewriter">
                {/* Game Start header with initial board */}
                <div className="log-game-start">
                    <span className="log-header-text">--- Game Start ---</span>
                    {initialBoardText && (
                        <div className="log-board-container">
                            <pre className="log-board-text">{initialBoardText}</pre>
                        </div>
                    )}
                </div>

                {/* Turns */}
                {logHistory.map((turn) => {
                    return (
                    <div key={turn.turnIndex} className="log-turn">
                        {/* Turn header */}
                        <div className={`log-turn-header player-${turn.player}`}>
                            <span className="log-turn-label">
                                Turn {turn.turnIndex}: {getPlayerName(turn.player)}
                            </span>
                            {turn.turnReasoning && (
                                <button
                                    className={`log-expand-btn ${expandedReasoning.has(`turn-${turn.turnIndex}`) ? 'expanded' : ''}`}
                                    onClick={(e) => { toggleReasoningExpand(`turn-${turn.turnIndex}`); e.currentTarget.blur(); }}
                                    title="Toggle reasoning"
                                    tabIndex={-1}
                                >
                                    {expandedReasoning.has(`turn-${turn.turnIndex}`) ? '[-]' : '[+]'}
                                </button>
                            )}
                        </div>

                        {/* Turn-level reasoning (expanded) - only for LLM players */}
                        {turn.turnReasoning && expandedReasoning.has(`turn-${turn.turnIndex}`) && (
                            <div className="log-reasoning">
                                <pre className="log-reasoning-text">{turn.turnReasoning}</pre>
                            </div>
                        )}

                        {/* Actions within the turn */}
                        {turn.actions.map((action, actionIdx) => {
                            const actionKey = `action-${turn.turnIndex}-${actionIdx}`;
                            return (
                                <div key={actionKey} className="log-action">
                                    {/* Move description */}
                                    <div className="log-action-header">
                                        <span className="log-action-desc">
                                            {action.moveDesc || `Move ${actionIdx + 1}`}
                                        </span>
                                        {(action.thinkingTime != null || action.tokenUsage != null) && (
                                            <span className="log-action-stats">
                                                {action.thinkingTime != null && <span className="log-stat">{action.thinkingTime.toFixed(1)}s</span>}
                                                {action.tokenUsage != null && <span className="log-stat">{action.tokenUsage.toLocaleString()} tok</span>}
                                            </span>
                                        )}
                                        {/* Only show expand button if action has reasoning */}
                                        {action.reasoning && (
                                            <button
                                                className={`log-expand-btn ${expandedReasoning.has(actionKey) ? 'expanded' : ''}`}
                                                onClick={(e) => { toggleReasoningExpand(actionKey); e.currentTarget.blur(); }}
                                                title="Toggle reasoning"
                                                tabIndex={-1}
                                            >
                                                {expandedReasoning.has(actionKey) ? '[-]' : '[+]'}
                                            </button>
                                        )}
                                    </div>

                                    {/* Action-level reasoning (expanded) - only for LLM players */}
                                    {action.reasoning && expandedReasoning.has(actionKey) && (
                                        <div className="log-action-reasoning">
                                            <pre className="log-reasoning-text">{action.reasoning}</pre>
                                        </div>
                                    )}

                                    {/* Board state - always shown */}
                                    {action.boardText && (
                                        <div className="log-board-container">
                                            <pre className="log-board-text">{action.boardText}</pre>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    );
                })}

                {/* LLM Thinking Indicator */}
                {llmThinking && (
                    <div className="log-llm-thinking">
                        <div className="log-thinking-header">
                            <span className="log-thinking-provider">
                                {llmProvider || 'AI'}{llmModel ? ` (${llmModel})` : ''}
                            </span>
                            <span className="log-thinking-label">thinking</span>
                            <span className="log-thinking-dots">
                                <span className="dot">.</span>
                                <span className="dot">.</span>
                                <span className="dot">.</span>
                            </span>
                        </div>
                    </div>
                )}

                {/* LLM Error */}
                {llmError && !llmThinking && (
                    <div className="log-llm-error">
                        <span className="log-error-label">Error:</span>
                        <span className="log-error-message">{llmError}</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="log-panel">
            <div className="tabs">
                <button
                    className={`${mode === 'chat' ? 'active' : ''} ${!chatEnabled && !tutorialMode ? 'disabled' : ''}`}
                    disabled={!chatEnabled && !tutorialMode}
                    onClick={(event) => {
                        if (!chatEnabled && !tutorialMode) return;
                        setMode('chat');
                        if (event?.currentTarget?.blur) {
                            event.currentTarget.blur();
                        }
                    }}
                >
                    {tutorialMode ? 'TUTORIAL' : 'CHAT'}
                </button>
                <button
                    className={mode === 'log' ? 'active' : ''}
                    onClick={(event) => {
                        setMode('log');
                        if (event?.currentTarget?.blur) {
                            event.currentTarget.blur();
                        }
                    }}
                >
                    LOG
                </button>
            </div>
            {puzzleMetadata && (
                <div className="puzzle-banner">
                    <div className="puzzle-banner__title">
                        {(() => {
                            const t = puzzleMetadata.puzzle_type || 'win';
                            if (t === 'threat') {
                                return `PUZZLE · disrupt P${puzzleMetadata.threat_player ?? '?'}'s ${puzzleMetadata.num_jumps}-jump threat`;
                            }
                            if (t === 'passthrough') {
                                return `PUZZLE · ${puzzleMetadata.num_jumps}-jump passthrough advance for P${puzzleMetadata.player}`;
                            }
                            if (t === 'denial') {
                                const K = puzzleMetadata.K ?? '?';
                                const M = puzzleMetadata.M ?? '?';
                                const pat = puzzleMetadata.pattern ? ` (${puzzleMetadata.pattern})` : '';
                                return `PUZZLE · denial${pat} · ${K}-loop + ${M}-advance for P${puzzleMetadata.player}`;
                            }
                            return `PUZZLE · ${puzzleMetadata.num_jumps}-jump win for P${puzzleMetadata.player}`;
                        })()}
                        <span className="puzzle-banner__seed"> · seed {puzzleMetadata.seed}</span>
                    </div>
                    <div className="puzzle-banner__solution">
                        {(!puzzleEvalResult && puzzleAttemptMode !== 'llm') ? (
                            <span className="puzzle-banner__hidden">
                                Solution hidden. Revealed after your attempt.
                            </span>
                        ) : (() => {
                            const t = puzzleMetadata.puzzle_type || 'win';
                            if (t === 'threat') {
                                const pivots = puzzleMetadata.canonical_pivots || [];
                                const chain = puzzleMetadata.chain_landings || [];
                                const start = puzzleMetadata.start_pos;
                                const fullPath = start ? [start, ...chain] : chain;
                                return (
                                    <>
                                        <span className="puzzle-banner__label">Threat line:</span>{' '}
                                        {fullPath.map((rc, i) => (
                                            <span key={`c${i}`} className="puzzle-banner__landing">
                                                {i > 0 && <span className="puzzle-banner__arrow"> → </span>}
                                                ({rc[0]},{rc[1]})
                                            </span>
                                        ))}
                                        <br />
                                        <span className="puzzle-banner__label">
                                            {pivots.length > 1 ? 'Pivots (any disrupts):' : 'Pivot:'}
                                        </span>{' '}
                                        {pivots.length === 0
                                            ? <span style={{ opacity: 0.5 }}>none</span>
                                            : pivots.map((rc, i) => (
                                                <span key={`p${i}`} className="puzzle-banner__landing">
                                                    {i > 0 && ' '}
                                                    <span className="puzzle-banner__step">⊕</span>
                                                    {' '}({rc[0]},{rc[1]})
                                                </span>
                                            ))}
                                    </>
                                );
                            }
                            // Default win-style display (also used by passthrough/denial).
                            const start = puzzleMetadata.start_pos;
                            const landings = puzzleMetadata.canonical_landings || [];
                            const path = start ? [start, ...landings] : landings;
                            const circled = ['①','②','③','④','⑤','⑥','⑦','⑧','⑨'];
                            const stepGlyph = (idx) => {
                                if (idx === 0) return '●';
                                const k = idx - 1;
                                return k < circled.length ? circled[k] : `(${idx})`;
                            };
                            const canonicalLabel = puzzleAttemptMode === 'llm' && !puzzleEvalResult
                                ? 'Canonical (your reference; LLM does not see):'
                                : 'Canonical:';
                            return (
                                <>
                                    <span className="puzzle-banner__label">{canonicalLabel}</span>{' '}
                                    {path.map((rc, i) => (
                                        <span key={i} className="puzzle-banner__landing">
                                            {i > 0 && (
                                                <span className="puzzle-banner__arrow"> → </span>
                                            )}
                                            <span className="puzzle-banner__step">{stepGlyph(i)}</span>
                                            {' '}({rc[0]},{rc[1]})
                                        </span>
                                    ))}
                                    {t === 'win' && (
                                        <span className="puzzle-banner__caveat">
                                            {' '}· other solutions may exist
                                        </span>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
            <div className="log-content-wrapper">
                <div className="log-content" ref={contentRef}>
                    {mode === 'log' ? (
                        renderLogContent()
                    ) : tutorialMode ? (
                        <div className="tutorial-mode">
                            {tutorialMode.actionRequired ? (
                                <div className={`tutorial-action-banner ${tutorialMode.stepDone ? 'done' : ''}`}>
                                    {tutorialMode.stepDone
                                        ? <span>&#10003; Done — advance to continue</span>
                                        : <span>Action required: {tutorialMode.hint}</span>
                                    }
                                </div>
                            ) : (
                                <div className="tutorial-action-banner continue">
                                    <span>Press <kbd>]</kbd> or <kbd>›</kbd> to continue</span>
                                </div>
                            )}
                            <div className="tutorial-header-bar">
                                <span className="tutorial-header-text">How to Play</span>
                                <div className="tutorial-nav">
                                    <button
                                        className="tutorial-nav-btn"
                                        onClick={(e) => { tutorialMode.onPrev?.(); e.currentTarget.blur(); }}
                                        disabled={tutorialMode.stepIndex <= 0}
                                        aria-label="Previous step"
                                        tabIndex={-1}
                                    >
                                        ‹
                                    </button>
                                    <span key={tutorialMode.stepIndex} className="tutorial-progress tutorial-progress-flash">{tutorialMode.stepIndex + 1} / {tutorialMode.totalSteps}</span>
                                    <button
                                        className="tutorial-nav-btn"
                                        onClick={(e) => { tutorialMode.onNext?.(); e.currentTarget.blur(); }}
                                        disabled={!tutorialMode.canAdvance}
                                        aria-label="Next step"
                                        tabIndex={-1}
                                    >
                                        ›
                                    </button>
                                </div>
                            </div>

                            <div className="tutorial-message">
                                <div className="tutorial-message-title">{tutorialMode.instruction}</div>
                                <div className="tutorial-message-detail">{tutorialMode.detail}</div>
                            </div>

                            {!tutorialMode.hideControls && (() => {
                                const ac = tutorialMode.activeControls || [];
                                const hi = (key) => ac.includes(key) ? ' tutorial-control-active' : '';
                                return (
                                <div className="tutorial-controls-ref">
                                    <div className="tutorial-controls-title">Controls</div>
                                    <div className="tutorial-controls-grid">
                                        <span className={hi('wasd')}><kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd> to move</span>
                                        <span className={hi('click')}><kbd>&#x271B;</kbd> Click to move</span>
                                        <span className={hi('zoom')}><kbd>&#x2195;</kbd> Scroll to zoom</span>
                                        <span className={hi('pan')}><kbd>&#x2725;</kbd> Drag to pan</span>
                                        <span className={hi('fit')}><kbd>F</kbd> Fit board</span>
                                        <span className={hi('center')}><kbd>C</kbd> Center</span>
                                        <span className={hi('place')}><kbd>P</kbd> Place</span>
                                        <span className={hi('jump')}><kbd>J</kbd> Jump</span>
                                        <span className={hi('confirm')}><kbd>ENTER</kbd> Confirm</span>
                                        <span className={hi('cancel')}><kbd>ESC</kbd> Cancel</span>
                                        <span className={hi('undo')}><kbd>⌫</kbd> Undo</span>
                                        <span className={hi('next')}><kbd>]</kbd> Next</span>
                                    </div>
                                </div>
                                );
                            })()}
                        </div>
                    ) : chatEnabled ? (
                        <div className="chat-mode">
                            <div className="chat-messages">
                                {chatMessages.length === 0 ? (
                                    <div className="chat-empty">No messages yet</div>
                                ) : (
                                    (() => {
                                        // Group messages by afterMove
                                        let lastMoveIdx = -1;
                                        return chatMessages.map((msg, i) => {
                                            const showDivider = msg.afterMove !== lastMoveIdx;
                                            lastMoveIdx = msg.afterMove;
                                            return (
                                                <React.Fragment key={i}>
                                                    {showDivider && (
                                                        <div className="chat-move-divider">
                                                            {msg.afterMove === 0 ? 'Before game' : `After move ${msg.afterMove}`}
                                                        </div>
                                                    )}
                                                    <div className={`chat-bubble ${msg.isOwn ? 'chat-bubble--own' : 'chat-bubble--other'}${msg.playerNum ? ` chat-bubble--p${msg.playerNum}` : ''}`}>
                                                        {!msg.isOwn && (
                                                            <span className="chat-bubble__name">{msg.displayName}</span>
                                                        )}
                                                        <span className="chat-bubble__text">{msg.message}</span>
                                                        <span className="chat-bubble__time" title={msg.timestamp}>
                                                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                </React.Fragment>
                                            );
                                        });
                                    })()
                                )}
                            </div>
                            {onChatSend && (
                                <div className="chat-input-bar">
                                    <input
                                        type="text"
                                        className="chat-input"
                                        value={chatInput}
                                        onChange={(e) => onChatInputChange?.(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') onChatSend(); }}
                                        placeholder="Type a message..."
                                        maxLength={500}
                                    />
                                    <button className="chat-send-btn" onClick={onChatSend} disabled={!chatInput?.trim()}>
                                        Send
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="log-placeholder">Chat is coming soon!</div>
                    )}
                </div>
                <div ref={scrollTrackRef} className="panel-scroll-track panel-scroll-track--left" onClick={handleTrackClick}>
                    <div
                        className={[
                            'panel-scroll-thumb',
                            isDragging ? 'dragging' : '',
                            maxScrollTop <= 0 ? 'disabled' : '',
                        ].filter(Boolean).join(' ')}
                        style={{
                            height: `${thumbHeight}px`,
                            top: `${thumbPosition}px`,
                        }}
                        onPointerDown={handleThumbPointerDown}
                    />
                </div>
            </div>
        </div>
    );
};

export default LogPanel;
